/**
 * Google One Tap + Continue with Google button hook.
 *
 * Loads the Google Identity Services (GIS) client library, initializes One Tap,
 * and provides a `promptOneTap()` and `renderButton()` interface.
 */
import { useEffect, useRef, useCallback, useState } from 'react';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

/** Callback shape from GIS credential response */
export interface GoogleCredentialResponse {
  credential: string; // JWT ID token
  select_by: string;  // 'auto' | 'user' | 'user_1tap' | 'btn' etc.
  clientId?: string;
}

interface UseGoogleOneTapOptions {
  /** Called with the Google credential JWT on success */
  onSuccess: (credential: string, selectBy: string) => void;
  /** Called on error */
  onError?: (error: string) => void;
  /** Whether to auto-show the One Tap prompt (default true) */
  autoPrompt?: boolean;
  /** Disable the hook entirely */
  disabled?: boolean;
}

// Track script loading globally (only load once)
let gisScriptLoaded = false;
let gisScriptLoading: Promise<void> | null = null;

// Track GIS initialization globally — survives component remounts.
// GIS only needs initialize() once per page load; calling it again after
// FedCM cooldown floods the browser with rejected prompts.
let gisInitialized = false;

function loadGisScript(): Promise<void> {
  if (gisScriptLoaded) return Promise.resolve();
  if (gisScriptLoading) return gisScriptLoading;

  gisScriptLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      gisScriptLoaded = true;
      resolve();
    };
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });

  return gisScriptLoading;
}

export function useGoogleOneTap({
  onSuccess,
  onError,
  autoPrompt = true,
  disabled = false,
}: UseGoogleOneTapOptions) {
  const [isReady, setIsReady] = useState(false);
  const initializedRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    if (disabled || !GOOGLE_CLIENT_ID) return;

    // If GIS was already initialized globally (e.g. user navigated back),
    // just mark ready and re-trigger the One Tap prompt.
    // initialize() is only needed once, but prompt() should fire on each mount
    // so the floating modal appears when navigating to login/register.
    // Chrome silently suppresses prompt() if in FedCM cooldown — no harm done.
    if (gisInitialized) {
      initializedRef.current = true;
      setIsReady(true);
      if (autoPrompt) {
        const google = (window as any).google;
        google?.accounts?.id?.prompt();
      }
      return;
    }

    if (initializedRef.current) return;

    let cancelled = false;

    loadGisScript()
      .then(() => {
        if (cancelled) return;
        const google = (window as any).google;
        if (!google?.accounts?.id) {
          onErrorRef.current?.('Google Identity Services not available');
          return;
        }

        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: GoogleCredentialResponse) => {
            if (response.credential) {
              onSuccessRef.current(response.credential, response.select_by);
            }
          },
          auto_select: false, // Don't auto-sign-in, just show prompt
          cancel_on_tap_outside: false,
          itp_support: true,
        });

        initializedRef.current = true;
        gisInitialized = true;
        setIsReady(true);

        // Show One Tap prompt with notification callback.
        // If FedCM is in cooldown, prompt() silently fails — that's OK,
        // the user can still click "Continue with Google" (uses popup OAuth).
        if (autoPrompt) {
          google.accounts.id.prompt((_notification: any) => {
            // Swallow FedCM cooldown/dismissal — not an error for the user.
            // The rendered GIS button (popup OAuth) is the reliable fallback.
          });
        }
      })
      .catch((err) => {
        if (!cancelled) {
          onErrorRef.current?.(err.message || 'Failed to load Google sign-in');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [disabled, autoPrompt]);

  /** Manually trigger the One Tap prompt */
  const promptOneTap = useCallback(() => {
    const google = (window as any).google;
    if (google?.accounts?.id) {
      google.accounts.id.prompt();
    }
  }, []);

  /** Render the "Continue with Google" button into a container element */
  const renderButton = useCallback((element: HTMLElement | null) => {
    if (!element || !isReady) return;
    const google = (window as any).google;
    if (!google?.accounts?.id) return;

    google.accounts.id.renderButton(element, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'continue_with',
      width: element.offsetWidth,
      logo_alignment: 'left',
    });
  }, [isReady]);

  return { isReady, promptOneTap, renderButton };
}
